import { supabaseService } from './supabaseService';

class AuthService {
  async login(credentials) {
    try {
      // Try to sign in with Supabase
      const result = await supabaseService.signIn(credentials.email, credentials.password);
      
      if (!result.user) {
        throw new Error('Invalid credentials');
      }

      // Get or create user profile
      let userProfile;
      try {
        userProfile = await supabaseService.getUserProfile(result.user.id);
      } catch (error) {
        // If profile doesn't exist, create one with default data
        userProfile = await supabaseService.createUserProfile(result.user.id, {
          name: result.user.user_metadata?.name || result.user.email.split('@')[0],
          email: result.user.email,
          roles: this.getDefaultRoles(result.user.email)
        });
      }

      const userData = {
        id: result.user.id,
        email: result.user.email,
        name: userProfile.name,
        roles: userProfile.roles || this.getDefaultRoles(result.user.email)
      };

      return {
        user: userData,
        token: result.session.access_token
      };
    } catch (error) {
      console.error('Login error:', error);
      
      // More specific error messages
      if (error.message.includes('Invalid login credentials')) {
        throw new Error('Invalid email or password');
      } else if (error.message.includes('Email not confirmed')) {
        throw new Error('Please check your email and confirm your account');
      } else if (error.message.includes('Too many requests')) {
        throw new Error('Too many login attempts. Please try again later');
      }
      
      throw new Error('Login failed. Please check your credentials and try again');
    }
  }

  async validateToken(token) {
    try {
      const user = await supabaseService.getCurrentUser();
      if (!user) {
        throw new Error('Invalid token');
      }

      // Get user profile
      let userProfile;
      try {
        userProfile = await supabaseService.getUserProfile(user.id);
      } catch (error) {
        // Create profile if it doesn't exist
        userProfile = await supabaseService.createUserProfile(user.id, {
          name: user.user_metadata?.name || user.email.split('@')[0],
          email: user.email,
          roles: this.getDefaultRoles(user.email)
        });
      }

      return {
        id: user.id,
        email: user.email,
        name: userProfile?.name || user.email.split('@')[0],
        roles: userProfile?.roles || this.getDefaultRoles(user.email)
      };
    } catch (error) {
      console.error('Token validation error:', error);
      throw new Error('Invalid token');
    }
  }

  async signUp(userData) {
    try {
      const result = await supabaseService.signUp(userData.email, userData.password, {
        name: userData.name
      });

      if (result.user) {
        // Create user profile
        await supabaseService.createUserProfile(result.user.id, {
          name: userData.name,
          email: userData.email,
          roles: userData.roles || ['Viewer']
        });
      }

      return { user: result.user };
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  }

  async logout() {
    try {
      await supabaseService.signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  // Assign default roles based on email domain for demo
  getDefaultRoles(email) {
    if (email === 'admin@company.com') {
      return ['Admin', 'Executive'];
    } else if (email === 'exec@company.com') {
      return ['Executive'];
    } else if (email.includes('@brioche.com')) {
      return ['Manager'];
    } else if (email.includes('@enjoy.com')) {
      return ['Viewer'];
    }
    return ['Viewer'];
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export const authService = new AuthService();